app.service("ShareData", [
	"localStorageService",
	function (localStorageService) {
		// The calendar's filters (practice, clinicians, view, status chip, dates on screen) are kept for the browser tab's
		// session, so a page refresh still opens the calendar where it was closed. Ids and view names only -
		// never the search text, which can hold a patient's name. Cleared on logout like everything else.
		const CALENDAR_FILTERS_KEY = "cattrax.calendarFilters";

		function emptyCalendarFilters() {
			return {
				homeDate: null,
				userCache: {},
				practiceCache: {},
				viewPrefs: {},
			};
		}

		function loadCalendarFilters() {
			try {
				const saved = JSON.parse(window.sessionStorage.getItem(CALENDAR_FILTERS_KEY));
				return saved ? { ...emptyCalendarFilters(), ...saved } : emptyCalendarFilters();
			} catch (e) {
				return emptyCalendarFilters();
			}
		}

		function persistCalendarFilters() {
			try {
				window.sessionStorage.setItem(CALENDAR_FILTERS_KEY, JSON.stringify(service.savedCalendarFilters));
			} catch (e) {
				// Storage blocked or full - the filters still last until the page is reloaded.
			}
		}

		// The task list's and waitlist's filters, kept in memory between opens of each. As with the calendar,
		// ids and view names only - never the search text.
		function emptyTaskFilters() {
			return {
				selectedPractice: null,
				tab: "open",
				label: "",
				assignee: "",
			};
		}

		function emptyWaitlistFilters() {
			return {
				selectedPractices: null,
				tab: "waiting",
				surgeon: "",
				booking: "",
			};
		}

		function forgetCalendarFilters() {
			try {
				window.sessionStorage.removeItem(CALENDAR_FILTERS_KEY);
			} catch (e) {}
		}

		var service = {
			clinicianIdHome: null,
			allergies: {
				patientId: null,
				allergyList: [],
				reload: false,
			},
			taskFilters: emptyTaskFilters(),
			waitlistFilters: emptyWaitlistFilters(),
			savedCalendarFilters: loadCalendarFilters(),
			// A booking set aside while its patient is registered (the booking's "Create new patient"), so the
			// calendar can pick it up again once they exist. Memory only: its note can hold clinical detail.
			bookingAwaitingPatient: null,
			// Each practice's clinicians who take appointments, by practice id, so the calendar's rail can draw
			// them straight away when the dialog opens or the practice changes - the calendar still asks
			// again each time and keeps the newer list. Staff names and practices only; memory, not storage.
			calendarClinicians: {},
			// Each practice's public holidays for a year, by "practiceId|year", so the calendar does not ask for
			// them again every time it opens. Memory only.
			calendarHolidays: {},

			// Called on logout and idle timeout. Everything here is per user, so anything left behind is
			// the last user's: their home-calendar clinician, task assignee/practice filters and waitlist
			// practices were otherwise applied to whoever logged in next on the same tab.
			destroy: function () {
				service.clinicianIdHome = null;
				service.allergies = { patientId: null, allergyList: [], reload: false };
				service.taskFilters = emptyTaskFilters();
				service.waitlistFilters = emptyWaitlistFilters();
				service.savedCalendarFilters = emptyCalendarFilters();
				forgetCalendarFilters();
				service.bookingAwaitingPatient = null;
				service.calendarClinicians = {};
				service.calendarHolidays = {};
			},
			getCalendarClinicians: function (practiceId) {
				const list = service.calendarClinicians[practiceId];
				return list ? angular.copy(list) : null;
			},
			setCalendarClinicians: function (practiceId, list) {
				service.calendarClinicians[practiceId] = angular.copy(list);
			},
			getCalendarHolidays: function (key) {
				const list = service.calendarHolidays[key];
				return list ? angular.copy(list) : null;
			},
			setCalendarHolidays: function (key, list) {
				service.calendarHolidays[key] = angular.copy(list);
			},

			setAllergies: function (patientId, allergyList) {
				service.allergies = { patientId, allergyList: angular.copy(allergyList), reload: false };
			},
			reloadAllergies: function (patientId) {
				service.allergies = { patientId, allergyList: [], reload: true };
			},
			clearAllergiesReload: function (patientId) {
				service.allergies = { patientId, allergyList: [], reload: false };
			},
			setHomeclinicianId: function (clinicianId) {
				service.clinicianIdHome = clinicianId;
			},
			setTaskFilters: function (filter) {
				service.taskFilters = { ...service.taskFilters, ...filter };
			},
			setSavedCalendarPractice: function (currentPractice,practiceId) {
				const setting = service.savedCalendarFilters.practiceCache[currentPractice];
				service.savedCalendarFilters.practiceCache[currentPractice] = {...setting,practiceId};
				persistCalendarFilters();
			},
			setSavedCalendarUser: function (currentPractice,userId) {
				const setting = service.savedCalendarFilters.practiceCache[currentPractice];
				service.savedCalendarFilters.practiceCache[currentPractice] = {...setting,userId};
				
				const obj = service.savedCalendarFilters.userCache[userId];
				service.savedCalendarFilters.userCache[userId] = { weight: (obj?.weight || 0) + 1 };
				persistCalendarFilters();
			},
			// Which clinicians were ticked when several were on screen at once - empty means everyone, unless
			// noClinicians says everyone was unticked.
			setSavedCalendarClinicians: function (currentPractice, clinicianIds, noClinicians) {
				const setting = service.savedCalendarFilters.practiceCache[currentPractice];
				service.savedCalendarFilters.practiceCache[currentPractice] = { ...setting, clinicianIds: clinicianIds.slice(), noClinicians: !!noClinicians };
				persistCalendarFilters();
			},
			// Which practices were ticked when several were on screen at once - empty means all of them, unless
			// noPractices says they were all unticked.
			setSavedCalendarPractices: function (currentPractice, practiceIds, noPractices) {
				const setting = service.savedCalendarFilters.practiceCache[currentPractice];
				service.savedCalendarFilters.practiceCache[currentPractice] = { ...setting, practiceIds: practiceIds.slice(), noPractices: !!noPractices };
				persistCalendarFilters();
			},
			// How the calendar dialog was last laid out (view, colouring) - so reopening it lands where the
			// user left it rather than back on the default week.
			setCalendarViewPrefs: function (prefs) {
				service.savedCalendarFilters.viewPrefs = { ...service.savedCalendarFilters.viewPrefs, ...prefs };
				persistCalendarFilters();
			},
			setHomeDefaultDate: function (strDate) {
				service.savedCalendarFilters.homeDate = strDate;
				persistCalendarFilters();
			},
			clearSavedCalendarFilters: function () {
				service.savedCalendarFilters = emptyCalendarFilters();
				forgetCalendarFilters();
			},
			setBookingAwaitingPatient: function (booking) {
				service.bookingAwaitingPatient = booking ? { ...booking, setAt: Date.now() } : null;
			},
			// The booking set aside, if it is recent enough to still be wanted - half an hour to register
			// someone. Left in place; the calendar takes it (takeBookingAwaitingPatient) when it opens.
			getBookingAwaitingPatient: function () {
				const booking = service.bookingAwaitingPatient;
				if (booking && Date.now() - booking.setAt > 30 * 60 * 1000) {
					service.bookingAwaitingPatient = null;
					return null;
				}
				return booking;
			},
			takeBookingAwaitingPatient: function () {
				const booking = service.getBookingAwaitingPatient();
				service.bookingAwaitingPatient = null;
				return booking;
			},
		};

		return service;
	},
]);
